/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler, TouchEventHandler} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ChevronRight from '@material-ui/icons/ChevronRight';

export interface IconRadioLabelProps {
  label: string | JSX.Element;
  onIconClick?: MouseEventHandler<HTMLDivElement>;
  icon?: JSX.Element;
}

const useStyles = makeStyles(theme => ({
  radio : {
    display        : 'flex',
    justifyContent : 'space-between',
    padding        : '5px 0px',
    maxWidth       : 'calc(100vw - 104px)'
  },
  radioLabel : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  radioChevron : {
    display    : 'flex',
    alignItems : 'center'
  }
}));

/**
 * @IconRadioLabel
 *
 * Abstracted Radio Label that allow for a user to pass
 * a clickable icon as the label.
 *
 * ```typescript
 * import {IconRadioLabel} from '@ion/dls/src/components/radio';
 *
 * <IconRadioLabel label='Choose me'
 *   onIconClick={handleIconClick}
 *   icon={<NewIcon />} />
 *
 * ```
 *
 * @param {string} label: radio label
 * @param {Function} onIconClick: callback for when icon is clicked
 * @param {JSX.Element} icon: overrides default chevron as radio labels icon
 * @returns {ReactNode}
 */
export const IconRadioLabel = ({
  label,
  onIconClick,
  icon = <ChevronRight />
}: IconRadioLabelProps) => {
  const classes = useStyles();

  return (
    <div className={classes.radio}>
      <span className={classes.radioLabel}>{label}</span>
      {
        onIconClick &&
          <div className={classes.radioChevron}
            onClick={onIconClick}>
            {icon}
          </div>
      }
    </div>
  );
};

export default IconRadioLabel;
